import { useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Clock, GraduationCap, Layers, CheckCircle2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { COURSE_DOMAINS } from "@/data/coursesData";
import { useEnquiryPopup } from "@/context/EnquiryPopupContext";
import { handleImageError } from "@/lib/imageFallback";

const badgeStyles = {
  "Best Seller": "from-amber-400 to-orange-500",
  Trending: "from-fuchsia-500 to-purple-600",
  New: "from-emerald-400 to-teal-500",
  Popular: "from-sky-400 to-blue-500",
  Premium: "from-violet-500 to-indigo-600",
  "Global Certification": "from-rose-500 to-red-600",
  "Industry Aligned": "from-cyan-400 to-blue-500",
};

export default function CourseCard({ course, index, onApply }) {
  const [open, setOpen] = useState(false);
  const domain = COURSE_DOMAINS.find((d) => d.key === course.domain) || COURSE_DOMAINS[0];
  const DomainIcon = Icons[domain.icon] || Icons.Sparkles;
  const badgeGrad = badgeStyles[course.badge] || "from-slate-500 to-slate-700";
  const { openPopup } = useEnquiryPopup();

  const handleApply = () => {
    onApply?.(course);
    openPopup({ course: course.title });
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, delay: (index % 9) * 0.05, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -8 }}
        className="group relative h-full rounded-[26px] p-[1px] overflow-hidden"
        data-testid={`course-card-${course.slug}`}
      >
        {/* animated gradient border */}
        <div
          className={`pointer-events-none absolute -inset-[40%] opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br ${domain.gradient} blur-2xl`}
        />
        <div className="relative h-full flex flex-col rounded-[25px] bg-white/80 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(10,61,145,0.08)] group-hover:shadow-[0_25px_60px_-15px_rgba(10,61,145,0.35)] transition-shadow duration-500 overflow-hidden">
          {/* Image */}
          <div className="relative h-44 overflow-hidden">
            <motion.img
              src={course.image}
              alt={course.title}
              loading="lazy"
              decoding="async"
              onError={(e) => handleImageError(e, course.title, domain.title)}
              className="h-full w-full object-cover scale-100 group-hover:scale-110 transition-transform duration-700 ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: -6 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + (index % 9) * 0.05 }}
              className={`absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r ${badgeGrad} text-white text-[11px] font-bold px-2.5 py-1 shadow-lg`}
            >
              <Sparkles className="h-3 w-3" /> {course.badge}
            </motion.div>

            {/* Domain chip */}
            <div className={`absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white text-[11px] font-semibold px-2.5 py-1`}>
              <DomainIcon className="h-3 w-3" /> {domain.title}
            </div>

            {/* Title over image */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="text-white font-display font-bold text-lg leading-snug drop-shadow-sm">
                {course.title}
              </h3>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 flex flex-col p-5 gap-4">
            <div className="flex items-center gap-3 text-[12px] text-slate-500 font-medium">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-[#B31942]" /> {course.duration}
              </span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span className="inline-flex items-center gap-1">
                <GraduationCap className="h-3.5 w-3.5 text-[#0A3161]" /> {course.level}
              </span>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{course.description}</p>

            <div className="flex flex-wrap gap-1.5">
              {course.skills.slice(0, 4).map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-[#0A3161]/5 text-[#0A3161] text-[11px] font-semibold px-2.5 py-1 border border-[#0A3161]/10"
                >
                  {s}
                </span>
              ))}
              {course.skills.length > 4 && (
                <span className="rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold px-2.5 py-1">
                  +{course.skills.length - 4} more
                </span>
              )}
            </div>

            <div className="mt-auto pt-2 flex items-center gap-2">
              <button
                data-testid={`course-view-details-${course.slug}`}
                onClick={() => setOpen(true)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#0A3161]/15 bg-white text-[#0A3161] text-sm font-semibold px-4 py-2.5 hover:bg-[#0A3161]/5 transition-colors"
              >
                View Details
              </button>
              <button
                data-testid={`course-apply-${course.slug}`}
                onClick={handleApply}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-[#B31942] to-[#940835] text-white text-sm font-semibold px-4 py-2.5 shadow-[0_10px_25px_rgba(179,25,66,0.35)] hover:shadow-[0_14px_32px_rgba(179,25,66,0.45)] hover:-translate-y-0.5 transition-all"
              >
                Apply Now <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Details Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl border-0 p-0 bg-white">
          <div className="relative h-40 overflow-hidden rounded-t-3xl">
            <img
              src={course.image}
              alt={course.title}
              loading="lazy"
              decoding="async"
              onError={(e) => handleImageError(e, course.title, domain.title)}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A3161]/90 via-[#0A3161]/30 to-transparent" />
            <div className={`absolute top-4 left-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r ${badgeGrad} text-white text-[11px] font-bold px-2.5 py-1`}>
              <Sparkles className="h-3 w-3" /> {course.badge}
            </div>
            <DialogHeader className="absolute bottom-3 left-4 right-4 text-left">
              <DialogTitle className="text-white font-display text-2xl">{course.title}</DialogTitle>
            </DialogHeader>
          </div>

          <div className="p-6 pt-4 space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-[#F8F9FA] p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Duration</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">{course.duration}</div>
              </div>
              <div className="rounded-xl bg-[#F8F9FA] p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Level</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">{course.level}</div>
              </div>
              <div className="rounded-xl bg-[#F8F9FA] p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Fee</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">{course.fee}</div>
              </div>
            </div>

            <p className="text-slate-600 leading-relaxed">{course.description}</p>

            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-3">
                <Layers className="h-4 w-4 text-[#B31942]" /> Key Skills
              </div>
              <div className="flex flex-wrap gap-2">
                {course.skills.map((s) => (
                  <span key={s} className="rounded-full bg-[#0A3161]/5 text-[#0A3161] text-xs font-semibold px-3 py-1.5 border border-[#0A3161]/10">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-3">
                <CheckCircle2 className="h-4 w-4 text-[#B31942]" /> Program Highlights
              </div>
              <ul className="grid sm:grid-cols-2 gap-2">
                {course.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {h}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-3">
                <GraduationCap className="h-4 w-4 text-[#B31942]" /> Curriculum
              </div>
              <div className="space-y-3">
                {course.curriculum.map((m, i) => (
                  <div key={m.module} className="rounded-2xl border border-black/5 p-4 bg-white soft-shadow">
                    <div className="text-sm font-bold text-[#0A3161] mb-1.5">
                      {i + 1}. {m.module}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {m.topics.map((t) => (
                        <span key={t} className="text-[11px] font-medium text-slate-500 bg-slate-50 rounded-full px-2.5 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              data-testid={`course-modal-apply-${course.slug}`}
              onClick={() => {
                setOpen(false);
                handleApply();
              }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#B31942] to-[#940835] text-white text-sm font-bold px-5 py-3.5 shadow-[0_14px_32px_rgba(179,25,66,0.35)] hover:-translate-y-0.5 transition-transform"
            >
              Apply Now <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
